/** The Defaults tab's trip settings, as data (backend plan 2026-10-07 §B): the editable
 *  config keys grouped by how tours use them, their words, and the pure steps of an edit —
 *  the stored value as a form token, the tokens as a PATCH (`set` / `unset` of exactly
 *  the changed paths), and the config the save dialog diffs.
 *
 *  Every editable setting is tri-state: **Not set** removes the key (tours keep their own
 *  value, or the franchise parent's / built-in value applies), or a value. The words for
 *  values and sources live here and nowhere else (§A's header uses them too). */
import type {
  OperatorTripDefaultKey,
  OperatorTripDefaultSetting,
  OperatorTripDefaultSource,
  OperatorTripDefaultsPatch,
} from '@/api/tourOperatorDefaults';
import type { FieldHelpEntry } from '@/components/FieldHelp';

import { DEFAULTS_HELP } from './fieldHelp';

export type EditableKey = Exclude<
  OperatorTripDefaultKey,
  'selfreg_verify_email' | 'selfreg_verify_phone'
>;
export type ReadOnlyKey = Extract<
  OperatorTripDefaultKey,
  'selfreg_verify_email' | 'selfreg_verify_phone'
>;

/** A form token: `unset` (Not set) or the value. */
export type BoolToken = 'unset' | 'on' | 'off';
export type LevelToken = 'unset' | '0' | '1' | '2';
export type Token = BoolToken | LevelToken;

export interface SettingDef {
  key: EditableKey;
  kind: 'bool' | 'level';
  label: string;
  help: FieldHelpEntry;
}

export interface SettingGroup {
  id: 'inherit' | 'applied' | 'fallback';
  title: string;
  /** When the group's values apply, in one sentence. */
  sentence: string;
  settings: readonly SettingDef[];
}

export const LEGACY_GROUP_CHAT = 'defaults.chat';

export const SETTING_GROUPS: readonly SettingGroup[] = [
  {
    id: 'inherit',
    title: 'How tours inherit',
    sentence:
      'Which tours get the operator defaults, and whether a tour’s own tag categories can change.',
    settings: [
      {
        key: 'use_operator_defaults',
        kind: 'bool',
        label: 'All tours use the operator defaults',
        help: DEFAULTS_HELP.use_operator_defaults,
      },
      {
        key: 'allow_tour_tag_categories_modification',
        kind: 'bool',
        label: 'Tours may change their own tag categories',
        help: DEFAULTS_HELP.allow_tour_tag_categories_modification,
      },
    ],
  },
  {
    id: 'applied',
    title: 'Applied while a tour uses the defaults',
    sentence:
      'These replace a tour’s own values in the guest app while the tour uses the operator defaults. Not set: tours keep their own.',
    settings: [
      {
        key: 'defaults.location_sharing',
        kind: 'level',
        label: 'Location sharing',
        help: DEFAULTS_HELP['defaults.location_sharing'],
      },
      {
        key: 'defaults.has_group_chat',
        kind: 'bool',
        label: 'Group chat',
        help: DEFAULTS_HELP['defaults.has_group_chat'],
      },
      {
        key: 'defaults.mytrips_display',
        kind: 'bool',
        label: 'Show bookings on My Trips',
        help: DEFAULTS_HELP['defaults.mytrips_display'],
      },
    ],
  },
  {
    id: 'fallback',
    title: 'Used when a tour sets nothing',
    sentence: 'Operator-wide values tours read when they have no setting of their own.',
    settings: [
      {
        key: 'show_all_users_to_guides_in_nonbespoke_tours',
        kind: 'bool',
        label: 'Guides see every guest on template tours',
        help: DEFAULTS_HELP.show_all_users_to_guides_in_nonbespoke_tours,
      },
      {
        key: 'disable_internal_guides_and_staff_conversations',
        kind: 'bool',
        label: 'Turn off internal (guides & staff) chat for every tour',
        help: DEFAULTS_HELP.disable_internal_guides_and_staff_conversations,
      },
    ],
  },
];

export const EDITABLE_SETTINGS: readonly SettingDef[] = SETTING_GROUPS.flatMap((g) => g.settings);

export const READ_ONLY_LABELS: Record<ReadOnlyKey, string> = {
  selfreg_verify_email: 'Verify guests’ email at self-registration',
  selfreg_verify_phone: 'Verify guests’ phone at self-registration',
};

// ---- words ------------------------------------------------------------------------

export const LEVEL_WORDS = ['Disabled', 'Optional', 'Important'] as const;

/** A value in words ("On", "Optional"); null / unknown → "—". */
export function valueWords(kind: SettingDef['kind'], value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (kind === 'level') {
    const n = Number(value);
    return LEVEL_WORDS[n] ?? String(value);
  }
  return value ? 'On' : 'Off';
}

/** Where a value comes from, in words ("Set here", "From Cascadia", "Built-in default",
 *  "From legacy key defaults.chat", "Not set — tours keep their own"). */
export function sourceWords(
  source: OperatorTripDefaultSource,
  parentName: string | null | undefined,
  sourcePath?: string | null,
): string {
  switch (source) {
    case 'own':
      return 'Set here';
    case 'parent':
      return `From ${parentName || 'the parent operator'}`;
    case 'fallback':
      return 'Built-in default';
    case 'legacy':
      return `From legacy key ${sourcePath ?? ''}`.trim();
    case 'unset':
      return 'Not set — tours keep their own';
  }
}

// ---- form ⇄ payload ------------------------------------------------------------------

/** RHF reads dots as nesting: a field name per key without them. */
export const fieldOf = (key: EditableKey) => key.replace(/\./g, '__');

export type FormValues = Record<string, Token>;

/** The stored own value as a token (`own` is the raw config value; null = absent). The
 *  token reads it the way the server's readers do — `bool()` / `int()`. */
export function tokenOf(def: SettingDef, own: unknown): Token {
  if (own === null || own === undefined) return 'unset';
  if (def.kind === 'level') {
    const n = Number(own);
    return n === 0 || n === 1 || n === 2 ? (String(n) as LevelToken) : 'unset';
  }
  return own ? 'on' : 'off';
}

export function formValuesOf(settings: readonly OperatorTripDefaultSetting[]): FormValues {
  const byKey = new Map(settings.map((s) => [s.key, s]));
  return Object.fromEntries(
    EDITABLE_SETTINGS.map((d) => [fieldOf(d.key), tokenOf(d, byKey.get(d.key)?.own ?? null)]),
  );
}

/** The settings whose token changed. */
export function changedSettings(initial: FormValues, values: FormValues): SettingDef[] {
  return EDITABLE_SETTINGS.filter((d) => values[fieldOf(d.key)] !== initial[fieldOf(d.key)]);
}

export type PatchOps = Pick<OperatorTripDefaultsPatch, 'set' | 'unset'> & {
  set: Record<string, boolean | 0 | 1 | 2>;
  unset: string[];
};

/** Exactly the changed paths: a value → `set`, Not set → `unset`. (The server also drops
 *  the legacy `defaults.chat` when `defaults.has_group_chat` is touched — never sent: it is
 *  not an editable path.) */
export function patchOps(initial: FormValues, values: FormValues): PatchOps {
  const set: PatchOps['set'] = {};
  const unset: string[] = [];
  for (const d of changedSettings(initial, values)) {
    const t = values[fieldOf(d.key)];
    if (t === 'unset' || t === undefined) unset.push(d.key);
    else if (d.kind === 'level') set[d.key] = Number(t) as 0 | 1 | 2;
    else set[d.key] = t === 'on';
  }
  return { set, unset };
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** `config` with the ops applied — what the save dialog diffs against the stored config,
 *  so it shows every key the PATCH will change, the legacy `defaults.chat` included. */
export function applyOps(
  config: Record<string, unknown> | null,
  ops: PatchOps,
): Record<string, unknown> {
  const out = JSON.parse(JSON.stringify(config ?? {})) as Record<string, unknown>;
  const setPath = (path: string, value: unknown) => {
    const parts = path.split('.');
    let node = out;
    for (const p of parts.slice(0, -1)) {
      if (!isObject(node[p])) node[p] = {};
      node = node[p] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]!] = value;
  };
  const unsetPath = (path: string) => {
    const parts = path.split('.');
    let node: unknown = out;
    for (const p of parts.slice(0, -1)) {
      if (!isObject(node)) return;
      node = node[p];
    }
    if (isObject(node)) delete node[parts[parts.length - 1]!];
  };
  for (const [path, value] of Object.entries(ops.set)) setPath(path, value);
  for (const path of ops.unset) unsetPath(path);
  if ('defaults.has_group_chat' in ops.set || ops.unset.includes('defaults.has_group_chat')) {
    unsetPath(LEGACY_GROUP_CHAT);
  }
  return out;
}
